
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Database } from "@/integrations/supabase/types";
import { RequestDetailsSheet } from "@/components/digitization/RequestDetailsSheet";
import { SmartPagination } from "@/components/ui/smart-pagination";
import { getPreviewUrl } from "@/utils/imageUtils";

type DigitizationStatus = Database["public"]["Enums"]["digitization_status"];

const ITEMS_PER_PAGE = 10; // Number of items per page

export default function Digitization() {
  const [statusFilter, setStatusFilter] = useState<DigitizationStatus | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>("all");
  const [creatorSearch, setCreatorSearch] = useState("");

  // Fetch creators for the dropdown
  const { data: creators } = useQuery({
    queryKey: ["creators-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("creator_id, name, surname, nick_name")
        .order("name");

      if (error) throw error;
      return data || [];
    },
  });

  const { data: requests, isLoading, refetch } = useQuery({
    queryKey: ["digitization-requests", statusFilter, currentPage, selectedCreatorId],
    queryFn: async () => {
      let query = supabase
        .from("digitization_requests")
        .select(`
          *,
          creators (
            name,
            surname,
            nick_name
          )
        `, { count: 'exact' })
        .order("created_at", { ascending: false });

      if (statusFilter) {
        query = query.eq("status", statusFilter);
      }

      if (selectedCreatorId !== "all") {
        query = query.eq("creator_id", parseInt(selectedCreatorId));
      }

      // Calculate pagination range
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      // Add pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      return {
        items: data || [],
        totalCount: count || 0
      };
    },
  });

  const totalPages = Math.ceil((requests?.totalCount || 0) / ITEMS_PER_PAGE);

  const getStatusBadgeColor = (status: DigitizationStatus) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "in_progress":
        return "bg-blue-500";
      case "completed":
        return "bg-green-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getArtistDisplayName = (creator: { name: string; surname: string; nick_name: string | null }) => {
    if (creator?.nick_name) {
      return `${creator.name} ${creator.surname} (${creator.nick_name})`;
    }
    return `${creator.name} ${creator.surname}`;
  };

  const filteredCreators = creators?.filter(creator => {
    const searchTerm = creatorSearch.toLowerCase();
    const fullName = getArtistDisplayName(creator).toLowerCase();
    return fullName.includes(searchTerm);
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Digitization Requests</h1>
          <div className="flex gap-4">
            <Select
              value={statusFilter || "all"}
              onValueChange={(value: string) => {
                setStatusFilter(value === "all" ? null : value as DigitizationStatus);
                setCurrentPage(1); // Reset to first page when changing filter
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={selectedCreatorId}
              onValueChange={(value) => {
                setSelectedCreatorId(value);
                setCurrentPage(1); // Reset to first page when changing creator
              }}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Filter by creator" />
              </SelectTrigger>
              <SelectContent>
                <Command>
                  <CommandInput 
                    placeholder="Search creators..."
                    value={creatorSearch}
                    onValueChange={setCreatorSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No creators found.</CommandEmpty>
                    <SelectItem value="all">All creators</SelectItem>
                    {filteredCreators?.map((creator) => (
                      <SelectItem 
                        key={creator.creator_id} 
                        value={creator.creator_id.toString()}
                      >
                        {getArtistDisplayName(creator)}
                      </SelectItem>
                    ))}
                  </CommandList>
                </Command>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Preview</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Dimensions</TableHead>
                  <TableHead>Request Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests?.items.map((request) => (
                  <TableRow key={request.request_id}>
                    <TableCell>
                      {request.preview ? (
                        <img
                          src={getPreviewUrl(request.preview)}
                          alt={request.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                          No preview
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {request.title}
                    </TableCell>
                    <TableCell>
                      {getArtistDisplayName(request.creators)}
                    </TableCell>
                    <TableCell>
                      {request.width_cm && request.height_cm 
                        ? `${request.width_cm} × ${request.height_cm} cm`
                        : "Not specified"
                      }
                    </TableCell>
                    <TableCell>
                      {format(new Date(request.request_date || ''), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(request.status)}>
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRequest(request);
                          setDetailsOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4">
                <SmartPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  className="justify-center"
                />
              </div>
            )}
          </Card>
        )}

        <RequestDetailsSheet
          request={selectedRequest}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          onStatusUpdate={refetch}
        />
      </div>
    </AdminLayout>
  );
}
